import { Injectable } from '@angular/core';
import {LocalTokenStoreService} from './local-token-store.service';
import {AccountInfoService} from './account-info.service';
import {AccessTokenService} from './access-token.service';
import {environment} from '../../environments/environment';
import {PlatformAesService} from './platform-aes.service';
import {WebsocketService} from '../websocket.service';
import {Router} from '@angular/router';
import {PartnerSettingsService} from '../partnerSettings/partner-settings.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private tokenKeyName = environment.accessTokenLocalStoreKey; // Token key name for localstorage

  constructor(private userService: AccountInfoService,
              private accessTokenService: AccessTokenService,
              private decryptionService: PlatformAesService,
              private websocketService: WebsocketService,
              private router: Router,
              private tokenService: LocalTokenStoreService,
              private partnerSettingsService: PartnerSettingsService) { }

  // resolves 'true' if authenticated
  initAuth() {
    return new Promise((resolve, reject) => {
      if (localStorage.getItem(this.tokenKeyName)) {
        const token = localStorage.getItem(this.tokenKeyName);
        if (this.tokenService.isValidHash(token)) {
          try {
            this.accessTokenService.setToken(this.tokenService.decryptToken(token));
            this.userService.isLoggedIn = true;
            Promise.all([this.userService.setAccountPermission(), this.userService.setAccountProfile()]).then(resp => {
              this.connectToKinger();
              this.userService.getKongApiKeyForUser();
              resolve(true);
            }, error => {
              this.clearLocalStorage();
              reject(error);
            });
          } catch {
            this.clearLocalStorage();
            reject('Invalid token. Please login again.');
          }
        }
      } else {
        this.clearLocalStorage();
        resolve(false);
      }
    });
  }

  saveAuth(token, user) {
    return new Promise((resolve, reject) => {
      this.accessTokenService.setToken(token);
      localStorage.setItem(this.tokenKeyName, this.tokenService.encryptToken(token));
      this.userService.isLoggedIn = true;
      if (user) {
        this.userService.setAccountDetails(user);
      }
      Promise.all([this.userService.setAccountPermission(), this.partnerSettingsService.initializePartnerSettings()]).then(resp => {
        this.connectToKinger();
        this.userService.getKongApiKeyForUser();
        resolve(true);
      }, error => {
        reject(error);
      });
    });
  }

  connectToKinger() {
    Promise.resolve(this.getKingerConnectionQueryString()).then(async response1 => {
      const temp = response1;
      this.websocketService.initializeKingerConnection(temp);
    }, error => {
      console.error('Websocket connection error: ' + error);
    });
  }

  getKingerConnectionQueryString() {
    return new Promise((resolve, reject) => {
        try {
          if (this.userService.kingerHash) {
            const parts = this.userService.kingerHash.split(':');
            if (parts.length === 3) {
              const hash = parts[0];
              const salt = parts[1];
              const iv = parts[2];
              if (hash && salt && iv) {
                resolve(this.decryptionService.decrypt(salt, iv, hash));
              } else {
                reject('Invalid hash');
              }
            } else {
              reject('Invalid hash length');
            }
          } else {
            reject('Hash not found');
          }
        } catch {
          reject('Invalid hash');
        }
    });
  }

  validateAndSaveAuth(email: string, env: string, password: string) {
    return new Promise((resolve, reject) => {
      Promise.resolve(this.userService.validateLogin(email, env, password)).then(loginResponse => {
        let response: any;
        response = loginResponse;
        Promise.resolve(this.saveAuth(response.accessToken, response.user)).then(saveResponse => {
          this.connectToKinger();
          this.userService.getKongApiKeyForUser();
          resolve(saveResponse);
        }, error => {
          reject(error);
        });
      }, error => {
        reject(error);
      });
    });
  }

  generateAuthenticatorToken(env: string, password: string, email = this.userService.email) {
    return new Promise((resolve, reject) => {
      if (email && env) {
        if (password) {
          Promise.resolve(this.userService.validateLogin(email, env, password)).then(loginResponse => {
            let authenticatorObject: any;
            authenticatorObject = loginResponse;
            const partner = authenticatorObject.user.partner;
            delete authenticatorObject['user'];
            const dt = new Date();
            dt.setMinutes(dt.getMinutes() + 523);
            authenticatorObject['exp'] = Date.parse(dt.toString()) / 1000;
            const authenticatorToken = this.tokenService.encryptAuthenticatorToken(authenticatorObject);
            resolve({token: encodeURIComponent(authenticatorToken), partner: partner});
          }, error => {
            reject(error);
          });
        } else {
          reject('Please enter a valid password');
        }
      } else {
        reject('Invalid User');
      }
    });
  }

  validateAndSaveAuthenticatorToken(encryptedToken) {
    return new Promise((resolve, reject) => {
      localStorage.clear();
      const token = this.tokenService.decryptAuthenticatorToken(encryptedToken);
      if (token && token.exp && token.accessToken) {
        this.accessTokenService.setToken(token.accessToken);
        const currentTime = new Date().getTime() / 1000;
        if (currentTime < token.exp) {
          Promise.resolve(this.userService.setAccountProfile()).then(resp => {
            Promise.resolve(this.saveAuth(this.accessTokenService.token, null)).then(saveResp => {
              this.userService.getKongApiKeyForUser();
              this.connectToKinger();
              resolve(true);
            }, error => {
              reject(error);
            });
          }, error => {
            reject(error);
          });
        } else {
          reject('Token expired');
        }
      } else {
        reject('Invalid token');
      }
    });
  }

  redirectWithAuthenticatorToken(isVerified, partner, token) {
    let redirectURL: string;
    if (isVerified) {
      if (environment.isOnPremise === false) {
        redirectURL = 'https://' + partner + '.apiplatform.io/authenticator?token=' + token;
      } else {
        redirectURL = '/authenticator?token=' + token;
      }
    } else {
      redirectURL = environment.platformRedirectURL + '/authenticator?token=' + token;
    }
    window.location.href = redirectURL;
  }

  // should be changed once all data are moved away from local storage

  clearLocalStorage() {
    localStorage.removeItem('partner');
    localStorage.removeItem('partnerId');
    localStorage.removeItem('Auth');
    localStorage.removeItem('partnerKey');
    localStorage.removeItem('currentPlan');
    localStorage.removeItem('account');
    localStorage.removeItem('domainPartner');
    localStorage.removeItem('sessionID');
    localStorage.removeItem('sessionStartTime');
    localStorage.removeItem('IdOfSession');
    localStorage.removeItem('signInEmail');
    localStorage.removeItem('apikey');
    localStorage.removeItem('logo');
    localStorage.removeItem('brandImage');
    localStorage.removeItem(this.tokenKeyName);
    localStorage.removeItem('availableEnvironmentsToSwitch');
    localStorage.removeItem('JWTToken');

    localStorage.removeItem('updatedBusLogAPI');
    localStorage.removeItem('workFlowAPI');
    localStorage.removeItem('b2bAPI');
    localStorage.removeItem('dmnAPI');
    localStorage.removeItem('sqlQueryAPI');
    localStorage.removeItem('thirdPartyAPI');
    localStorage.removeItem('businessLogicAPI');
    localStorage.removeItem('testSuiteAPI');
    localStorage.removeItem('isBusLogAPISet');
    localStorage.removeItem('dataAccessAPI');
    localStorage.removeItem('currentPlanDisplayName');
    localStorage.removeItem('selectDB');
  }

  logout() {
    this.websocketService.disconnect();
    this.clearLocalStorage();

    // Included the below snippet under timeout to ensure that logging out is done seamlessly if multiple tabs are opened
    setTimeout(() => {
      if (localStorage.getItem('setLogOutFromAllTabs')) {
        localStorage.removeItem('setLogOutFromAllTabs');
      }
      // this.router.navigate(['/auth/login']);
      window.location.href = environment.staticSite;
    }, 1000);
  }
}
